import { useEffect, useState } from "react";
import { useAuth } from "./auth.js";
import { type PublicAccount } from "./api.js";
import { type Locale, LOCALES } from "./i18n.js";
import { LocaleProvider, useLocale } from "./ui.js";
import { LoginPage } from "./pages/Login.js";
import { FirstRunOnboarding } from "./pages/Onboarding.js";
import { SignupPage } from "./pages/Signup.js";
import { ForgotPage } from "./pages/Forgot.js";
import { Dashboard } from "./pages/Dashboard.js";
import { WorkspacePanel } from "./panels/WorkspacePanel.js";
import { OwnerStopPage } from "./pages/OwnerStop.js";
import { MobileAppPage } from "./pages/MobileApp.js";
import { ChatPage } from "./pages/Chat.js";
import {
  ActivitySurface,
  AppsSurface,
  AutomationsSurface,
  CodeSurface,
  DesignSurface,
  FilesSurface,
  ProjectsSurface,
} from "./surfaces/surfaces.js";
import { SettingsPage } from "./pages/Settings.js";
import { SkillsPage } from "./pages/Skills.js";
import { HelpPage } from "./pages/Help.js";
import { SupportInboxPage } from "./pages/SupportInbox.js";
import { SuperAdminPage } from "./pages/SuperAdmin.js";
import { useSupportAccess } from "./supportAccess.js";

export type AuthView = "login" | "signup" | "forgot";

const LOCALE_KEY = "ss.web.locale.v1";

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LOCALE_KEY);
  return LOCALES.includes(stored as Locale) ? (stored as Locale) : "en";
}

function persistLocale(locale: Locale): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCALE_KEY, locale);
}

/** F4A-4 — the mobile STOP page lives at the bookmarkable hash route `#stop`. */
function useHashRoute(): string {
  const [hash, setHash] = useState(() => (typeof window !== "undefined" ? window.location.hash : ""));
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return hash;
}

interface ImportMetaWithEnv extends ImportMeta {
  readonly env?: {
    readonly VITE_DEMO?: string;
  };
}

/**
 * #support surface — gated on the server-authoritative support capability
 * (`GET /api/support/access`), never on the login role: a normal account
 * with an activated support-staff grant gets the Inbox; a privileged login
 * without the grant gets the customer Help Center. Fail-closed while the
 * truth is still loading.
 */
function SupportSurface() {
  const { access } = useSupportAccess();
  return access.canUseInbox ? <SupportInboxPage /> : <HelpPage />;
}

const isDemoBuild = (import.meta as ImportMetaWithEnv).env?.VITE_DEMO === "true";

function Gate() {
  const { ready, account, setSession, isOwner, refresh } = useAuth();
  const { tr } = useLocale();
  const [view, setView] = useState<AuthView>("login");
  const hash = useHashRoute();
  const isDemo = typeof window !== "undefined" && (isDemoBuild || new URLSearchParams(window.location.search).get("demo") === "1");
  const wantsStop = hash === "#stop" || hash === "#/stop";
  const wantsMobile = hash.startsWith("#mobile") || hash.startsWith("#/mobile");
  const wantsWorkspace = hash === "#workspace" || hash === "#/workspace";
  const wantsSettings = hash === "#settings" || hash === "#/settings";
  const wantsUsage = hash === "#usage" || hash === "#/usage";
  const wantsSkills = hash === "#skills" || hash === "#/skills";
  const wantsDashboard = hash === "#dashboard" || hash === "#/dashboard";
  const wantsProjects = hash === "#projects" || hash === "#/projects";
  const wantsFiles = hash === "#files" || hash === "#/files";
  const wantsAutomation = hash === "#automation" || hash === "#/automation";
  const wantsApps = hash === "#apps" || hash === "#/apps";
  const wantsCode = hash === "#code" || hash === "#/code";
  const wantsDesign = hash === "#design" || hash === "#/design";
  const wantsActivity = hash === "#activity" || hash === "#/activity";
  const wantsAdmin = hash === "#admin" || hash === "#/admin";
  const wantsHelp = hash === "#help" || hash === "#/help";
  const wantsSupport = hash === "#support" || hash === "#/support";

  if (wantsMobile) {
    return <MobileAppPage />;
  }

  if (!account && isDemo) {
    const demoAccount: PublicAccount = {
      email: "preview@smartshikka.com",
      role: "user",
      status: "active",
      createdAt: new Date().toISOString(),
      approvedAt: new Date().toISOString(),
      approvedBy: null,
    };
    setSession(demoAccount);
    return (
      <div className="center-shell">
        <div className="card">{tr("loading")}</div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="center-shell">
        <div className="card">{tr("loading")}</div>
      </div>
    );
  }

  if (account && account.status === "active") {
    let page: React.JSX.Element;
    if (wantsStop) page = <OwnerStopPage />;
    else if (wantsWorkspace) page = <WorkspacePanel />;
    else if (wantsSettings || wantsUsage) {
      page = <SettingsPage initialSection={wantsUsage ? "usage" : "general"} />;
    }
    else if (wantsSkills) page = <SkillsPage />;
    else if (wantsAdmin && isOwner) page = <SuperAdminPage />;
    else if (wantsHelp) page = <HelpPage />;
    else if (wantsSupport) page = <SupportSurface />;
    else if (wantsDashboard) page = <Dashboard />;
    // PR-CORE-11: every product surface is a real page fed by canonical
    // durable endpoints — no fake-mature placeholders.
    else if (wantsProjects) page = <ProjectsSurface />;
    else if (wantsFiles) page = <FilesSurface />;
    else if (wantsAutomation) page = <AutomationsSurface />;
    else if (wantsApps) page = <AppsSurface />;
    else if (wantsCode) page = <CodeSurface />;
    else if (wantsDesign) page = <DesignSurface />;
    else if (wantsActivity) page = <ActivitySurface />;
    // REF-01: normal authenticated entry is the chat-first Smart Shikka shell.
    else page = <ChatPage />;
    return (
      <>
        {page}
        {/* Canonical first-run gate: the membership onboarding_done flag is
            the only record — no second local/dismissed state is kept. */}
        {account.onboardingDone === false ? (
          <FirstRunOnboarding account={account} onDone={() => void refresh()} />
        ) : null}
      </>
    );
  }

  if (view === "signup") return <SignupPage onNavigate={setView} />;
  if (view === "forgot") return <ForgotPage onNavigate={setView} />;
  return <LoginPage onNavigate={setView} />;
}

export function App() {
  const [locale, setLocale] = useState<Locale>(() => getInitialLocale());
  useEffect(() => { persistLocale(locale); }, [locale]);
  return (
    <LocaleProvider locale={locale} setLocale={setLocale}>
      <div className="ss-page-root">
        <div className="ss-page-app">
          <Gate />
        </div>
      </div>
    </LocaleProvider>
  );
}
